import React, { useContext, useEffect, useState, lazy, Suspense } from 'react';
import { styled } from '@mui/material/styles';
import { Route } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import {
  Card,
  CardContent,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import PrintIcon from '@mui/icons-material/Print';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import IconButton from '@mui/material/IconButton';

import { dateFormat } from '../ExportXLS/ExportXLS';
import { statusLabel, sourceLabel, badgeStatusLabel } from '../../visitorStatus';
import ColumnSettings from './ColumnSettings';

import Fab from '@mui/material/Fab';
import ListItemText from '@mui/material/ListItemText';
import { Link as RouterLink } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import DropDownMenu from '@/components/DropDownMenu/DropDownMenu';
import TablePagination from '@mui/material/TablePagination';
import Checkbox from '@mui/material/Checkbox';
import filter from 'common/filterVisitors';
import { MeetupContext } from '@/MeetupContext';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import { AppContext } from 'AppContext';
import { SystemFieldType } from '@/api';

const PREFIX = 'VisitorsTable';

const classes = {
  root: `${PREFIX}-root`,
  content: `${PREFIX}-content`,
  inner: `${PREFIX}-inner`,
  nameContainer: `${PREFIX}-nameContainer`,
  avatar: `${PREFIX}-avatar`,
  actions: `${PREFIX}-actions`,
  fab: `${PREFIX}-fab`,
  modal: `${PREFIX}-modal`,
  paper: `${PREFIX}-paper`,
  photo: `${PREFIX}-photo`,
  stickyAction: `${PREFIX}-stickyAction`,
  stickyActionHead: `${PREFIX}-stickyActionHead`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.root}`]: {
    overflowX: 'auto',
  },

  [`& .${classes.content}`]: {
    padding: 0,
  },

  [`& .${classes.inner}`]: {
    minWidth: 600,
  },

  [`& .${classes.nameContainer}`]: {
    display: 'flex',
    alignItems: 'center',
  },

  [`& .${classes.avatar}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.actions}`]: {
    justifyContent: 'flex-end',
  },

  [`& .${classes.fab}`]: {
    margin: theme.spacing(0.5),
    height: '36px !important',
    width: '36px !important',
    '& svg': {
      height: '24px !important',
      width: '24px !important',
    },
  },

  [`& .${classes.modal}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.paper}`]: {
    backgroundColor: theme.palette.background.paper,
    border: '2px solid #000',
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
  },

  [`& .${classes.photo}`]: {
    maxWidth: '70px',
    maxHeight: '70px',
    borderRadius: '7%',
  },

  // Колонка с кнопкой печати / меню действий закреплена справа,
  // чтобы оставаться видимой при горизонтальной прокрутке широкой таблицы.
  [`& .${classes.stickyAction}`]: {
    position: 'sticky',
    right: 0,
    zIndex: 2,
    // Колонка занимает ровно столько, сколько нужно двум кнопкам в один ряд
    // (узкие отступы + nowrap), чтобы не раздувать таблицу и не вызывать лишний скролл.
    width: '1%',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '-6px 0 8px -6px rgba(0, 0, 0, 0.25)',
  },

  // В заголовке таблицы фон отличается от тела — повторяем его,
  // чтобы закреплённая ячейка не выделялась белым.
  [`& .${classes.stickyActionHead}`]: {
    backgroundColor: theme.palette.grey[50],
  },
}));

const BadgeCardForVisitor = lazy(
  () => import('../../../BadgeDetails/components/BadgeViewWithDownload/BadgeViewForVisitor')
);

export const makeFIO = visitor => {
  if (!visitor) {
    return '';
  }
  return [visitor.lastName, visitor.firstName, visitor.middleName || ''].join(' ').trim();
  // if (visitor.middleName) {
  //   return [visitor.lastName, visitor.firstName, (visitor.middleName || "")].join(" ");
  // } else {
  //   return [visitor.firstName, visitor.lastName].join(" ");
  // }
};

const VisitorsTable = props => {
  const {
    className,
    meetupId,
    visitors: originalVisitors,
    handleDelete,
    selected,
    setSelected,
    setDeleteConf,
    search,
    group,
    statusFilter,
    ...rest
  } = props;

  const meetupContext = useContext(MeetupContext);
  const appContext = useContext(AppContext);

  const [modal, setModal] = useState({});
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    parseInt(localStorage.getItem('visitors-rows-per-page') || '10')
  );
  const [selectedAll, setSelectedAll] = React.useState(false);
  // Сортировка по любой колонке. По умолчанию — по дате добавления, новые сверху.
  const [sortField, setSortField] = React.useState('registrationTime');
  const [sortDir, setSortDir] = React.useState('desc');
  const [columnsAnchor, setColumnsAnchor] = React.useState(null);

  const registeredAt = visitor => {
    const t = visitor.status && visitor.status.registrationTime;
    return t ? new Date(t).getTime() : 0;
  };

  const optionalFields = meetupContext.fields.filter(
    f =>
      f.systemField !== SystemFieldType.FirstName &&
      f.systemField !== SystemFieldType.LastName &&
      f.systemField !== SystemFieldType.MiddleName &&
      f.systemField !== SystemFieldType.Group &&
      f.systemField !== SystemFieldType.QrToken
  );

  // Описание всех доступных колонок: подпись, как сортировать и как отрисовать.
  // Системные поля (ID, QR-код) доступны для добавления, но по умолчанию скрыты.
  const allColumns = [
    {
      id: 'fio',
      label: 'ФИО',
      sortValue: v => makeFIO(v).toLowerCase(),
      render: v => (
        <RouterLink to={'/m/' + meetupId + '/visitor/id/' + v.visitorId}>
          <ListItemText primary={makeFIO(v)} data-test-id='visitors-fio-cell' />
        </RouterLink>
      ),
    },
    {
      id: 'group',
      label: 'Группа',
      sortValue: v => (v.groupName || '').toLowerCase(),
      render: v => <ListItemText primary={v.groupName} />,
    },
    ...optionalFields.map(f => {
      const id = f.systemField || f.name;
      const isPhoto = f.systemField === SystemFieldType.Photo;
      const value = v => (f.systemField ? v[f.systemField] : (v.extended || {})[f.name]);
      return {
        id,
        label: f.label,
        sortable: !isPhoto, // фото не сортируем
        sortValue: v => {
          const raw = value(v);
          return raw == null ? '' : String(raw).toLowerCase();
        },
        render: isPhoto
          ? v => v.photo && <img className={classes.photo} src={'/api/image/' + v.photo} alt='' />
          : v => <ListItemText primary={value(v)} />,
      };
    }),
    {
      id: 'registrationTime',
      label: 'Дата добавления',
      sortValue: v => registeredAt(v),
      render: v => (
        <ListItemText primary={v.status && v.status.registrationTime ? dateFormat(v.status.registrationTime) : ''} />
      ),
    },
    {
      id: 'status',
      label: 'Статус',
      sortValue: v => statusLabel(v.status && v.status.status).toLowerCase(),
      render: v => <ListItemText primary={statusLabel(v.status && v.status.status)} />,
    },
    {
      id: 'source',
      label: 'Источник',
      system: true,
      sortValue: v => sourceLabel(v.status && v.status.source).toLowerCase(),
      render: v => <ListItemText primary={sourceLabel(v.status && v.status.source)} />,
    },
    {
      id: 'badgeStatus',
      label: 'Бейдж',
      system: true,
      sortValue: v => badgeStatusLabel(v.status && v.status.badgeStatus).toLowerCase(),
      render: v => <ListItemText primary={badgeStatusLabel(v.status && v.status.badgeStatus)} />,
    },
    {
      id: 'visitorId',
      label: 'ID',
      system: true,
      sortValue: v => Number(v.visitorId) || 0,
      render: v => <ListItemText primary={v.visitorId} />,
    },
    {
      id: 'qrToken',
      label: 'QR-код',
      system: true,
      sortValue: v => (v.qrToken || '').toLowerCase(),
      render: v => <ListItemText primary={v.qrToken} />,
    },
  ];
  const columnById = Object.fromEntries(allColumns.map(c => [c.id, c]));
  const defaultVisible = id => !columnById[id].system; // системные поля скрыты по умолчанию

  // Конфигурация колонок (порядок + видимость) хранится на сервере в settings.tableFields.
  const serverColumns = meetupContext.tableFields ?? [];
  const [savedColumns, setSavedColumns] = React.useState(() =>
    Array.isArray(serverColumns) ? serverColumns : []
  );

  // Сверяем сохранённую конфигурацию с реально доступными колонками:
  // выкидываем исчезнувшие, дописываем новые в конец.
  const columnConfig = [
    ...savedColumns.filter(c => columnById[c.id]),
    ...allColumns
      .filter(c => !savedColumns.some(s => s.id === c.id))
      .map(c => ({ id: c.id, visible: defaultVisible(c.id) })),
  ];
  const visibleColumns = columnConfig.filter(c => c.visible).map(c => columnById[c.id]);

  const persistColumns = next => {
    setSavedColumns(next);
    appContext.configApi.setTableFieldsSettings(meetupId, next).catch(() => {});
  };
  const toggleColumn = id => persistColumns(columnConfig.map(c => (c.id === id ? { ...c, visible: !c.visible } : c)));
  const moveColumn = (id, dir) => {
    const i = columnConfig.findIndex(c => c.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= columnConfig.length) return;
    const next = [...columnConfig];
    [next[i], next[j]] = [next[j], next[i]];
    persistColumns(next);
  };
  const columnSettingsItems = columnConfig.map(c => ({ id: c.id, label: columnById[c.id].label, visible: c.visible }));

  const isSortable = col => col.sortable !== false;
  // Клик по заголовку: тот же столбец — меняем направление, новый — сортируем по нему.
  const requestSort = id => {
    const col = columnById[id];
    if (!col || !isSortable(col)) return;
    if (sortField === id) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(id);
      setSortDir(id === 'registrationTime' ? 'desc' : 'asc');
    }
  };

  const getSortValue = (columnById[sortField] || columnById.registrationTime).sortValue;
  const filtered = filter(originalVisitors, search, group);
  const byStatus = statusFilter ? filtered.filter(v => v.status && v.status.status === statusFilter) : filtered;
  const visitors = [...byStatus].sort((a, b) => {
    const va = getSortValue(a);
    const vb = getSortValue(b);
    const cmp = typeof va === 'number' && typeof vb === 'number' ? va - vb : String(va).localeCompare(String(vb), 'ru');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Заголовок-колонка с переключателем сортировки (или без, если сортировка недоступна).
  const renderHead = col =>
    isSortable(col) ? (
      <TableCell key={col.id} sortDirection={sortField === col.id ? sortDir : false}>
        <TableSortLabel
          active={sortField === col.id}
          direction={sortField === col.id ? sortDir : 'asc'}
          onClick={() => requestSort(col.id)}
        >
          {col.label}
        </TableSortLabel>
      </TableCell>
    ) : (
      <TableCell key={col.id}>{col.label}</TableCell>
    );

  const handleClose = () => {
    setModal({});
  };
  const showModal = visitorId => {
    setModal({ visitorId: visitorId });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    localStorage.setItem('visitors-rows-per-page', event.target.value);
    setPage(0);
  };

  const isSelected = visitorId => {
    if (selected[visitorId]) {
      return true;
    }
    return false;
  };
  const handleSelect = (visitorId, checked) => {
    selected[visitorId] = checked;
    let newSelected = {};
    visitors
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      .map(e => e.visitorId)
      .forEach(e => {
        if (selected[e]) {
          newSelected[e] = selected[e];
        }
      });
    setSelected(newSelected);
    setSelectedAll(false);
  };

  const isSelectedAll = () => {
    return selectedAll;
  };
  const handleSelectAll = checked => {
    if (checked) {
      let newSelected = {};
      visitors.forEach(e => {
        newSelected[e.visitorId] = true;
      });
      setSelected(newSelected);
      setSelectedAll(true);
    } else {
      setSelected({});
      setSelectedAll(false);
    }
  };

  useEffect(() => {
    if (visitors.length < page * rowsPerPage) {
      setPage(0);
    }
  }, [visitors, page]);

  return (
    <StyledCard {...rest} className={clsx(classes.root, className)}>
      <CardContent className={classes.content}>
        <div className={classes.inner}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'>
                  <Checkbox checked={isSelectedAll()} onChange={e => handleSelectAll(e.target.checked)} />
                </TableCell>
                {visibleColumns.map(renderHead)}
                <TableCell className={clsx(classes.stickyAction, classes.stickyActionHead)}>
                  <IconButton size='small' aria-label='Настроить поля' onClick={e => setColumnsAnchor(e.currentTarget)}>
                    <ViewColumnIcon fontSize='small' />
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visitors.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map(visitor => (
                <TableRow className={classes.tableRow} hover key={visitor.visitorId}>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      checked={isSelected(visitor.visitorId)}
                      onChange={e => handleSelect(visitor.visitorId, e.target.checked)}
                    />
                  </TableCell>
                  {visibleColumns.map(col => (
                    <TableCell key={col.id}>{col.render(visitor)}</TableCell>
                  ))}

                  <TableCell className={classes.stickyAction}>
                    <Fab
                      size='small'
                      color='secondary'
                      aria-label='print'
                      className={classes.fab}
                      onClick={() => showModal(visitor.visitorId)}
                    >
                      <PrintIcon />
                    </Fab>
                    <DropDownMenu>
                      <MenuItem>
                        <RouterLink to={'/m/' + meetupId + '/visitor/id/' + visitor.visitorId}>
                          <ListItemText primary='Редактировать' />
                        </RouterLink>
                      </MenuItem>
                      <MenuItem onClick={() => setDeleteConf(visitor.visitorId)}>
                        <ListItemText primary='Удалить' />
                      </MenuItem>
                      <MenuItem onClick={() => showModal(visitor.visitorId)}>
                        <ListItemText primary='Печатать бейдж' />
                      </MenuItem>
                    </DropDownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component='div'
          count={visitors.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'previous page',
          }}
          nextIconButtonProps={{
            'aria-label': 'next page',
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </CardContent>
      <Modal
        aria-labelledby='simple-modal-title'
        aria-describedby='simple-modal-description'
        open={!!modal.visitorId}
        onClose={handleClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Suspense fallback={<LoadingSpinner />}>
          <div
            style={{
              backgroundColor: '#fff',
              border: '2px solid #000',
              boxShadow:
                '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
              padding: '16px 32px 24px',
            }}
          >
            <BadgeCardForVisitor meetupId={meetupId} visitorId={modal.visitorId} />
          </div>
        </Suspense>
      </Modal>
      <ColumnSettings
        open={!!columnsAnchor}
        anchorEl={columnsAnchor}
        onClose={() => setColumnsAnchor(null)}
        items={columnSettingsItems}
        onToggle={toggleColumn}
        onMove={moveColumn}
      />
    </StyledCard>
  );
};

VisitorsTable.propTypes = {
  className: PropTypes.string,
  visitors: PropTypes.array.isRequired,
  handleDelete: PropTypes.func,
};

export default VisitorsTable;
